import React, { useEffect, useState } from 'react';
import { RefreshCw } from 'lucide-react';
import ErrorBoundary from './ErrorBoundary';
import { fetchFundingRates } from '../lib/coinglass';
import { FundingRateSimple } from '../types/coinglass';

const FundingRateHeatmap: React.FC = () => {
  const [data, setData] = useState<FundingRateSimple[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lastUpdate, setLastUpdate] = useState<string | null>(null);

  const fetchData = async () => {
    try {
      setIsLoading(true);
      const result = await fetchFundingRates('BTC');
      
      // Sort by absolute funding rate
      const sortedData: FundingRateSimple[] = result
        .sort((a: FundingRateSimple, b: FundingRateSimple) => Math.abs(b.fundingRate) - Math.abs(a.fundingRate));
      
      setData(sortedData);
      setLastUpdate(new Date().toLocaleString());
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load data');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, 5 * 60 * 1000); // Refresh every 5 minutes
    return () => clearInterval(interval);
  }, []);

  const getHeatmapColor = (rate: number): string => {
    const absRate = Math.abs(rate);
    if (rate > 0) {
      if (absRate > 0.01) return 'bg-green-500/20 text-green-400';
      if (absRate > 0.005) return 'bg-green-500/10 text-green-300';
      return 'bg-green-500/5 text-green-200';
    } else {
      if (absRate > 0.01) return 'bg-red-500/20 text-red-400';
      if (absRate > 0.005) return 'bg-red-500/10 text-red-300';
      return 'bg-red-500/5 text-red-200';
    }
  };

  if (error) return null;

  if (isLoading || !data) return null;

  return (
    <div className="bg-zinc-900 rounded-lg border border-zinc-800 p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h3 className="text-lg font-semibold text-white">Funding Rate Heatmap</h3>
          <p className="text-sm text-gray-400 mt-1">
            Current funding rates across major exchanges
          </p>
        </div>
        <button
          onClick={fetchData}
          className="p-2 text-gray-400 hover:text-yellow-400 transition-colors"
          aria-label="Refresh data"
        >
          <RefreshCw size={20} />
        </button>
      </div>

      <div className="grid gap-2">
        {data.map(({ exchangeName, fundingRate }) => (
          <div
            key={exchangeName}
            className={`flex items-center justify-between p-3 rounded-lg ${getHeatmapColor(fundingRate)}`}
          >
            <span className="font-medium">{exchangeName}</span>
            <span className="font-mono">
              {(fundingRate * 100).toFixed(4)}%
            </span>
          </div>
        ))}
      </div>

      {lastUpdate && (
        <div className="text-xs text-gray-400 mt-4">
          Last updated: {lastUpdate}
        </div>
      )}
    </div>
  );
};

const FundingRateHeatmapWithErrorBoundary: React.FC = () => (
  <ErrorBoundary>
    <FundingRateHeatmap />
  </ErrorBoundary>
);

export default FundingRateHeatmapWithErrorBoundary;